import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth, isTerminalActivated } from '../../context/AuthContext';
import api from '../../api/axiosConfig';
import productAPI from '../../api/productAPI';
import restaurantAPI from '../../api/restaurantAPI';
import { getContextOptions } from '../../api/authAPI';
import { useShift } from '../../hooks/useShift';
import { useRestaurantCart } from '../../hooks/useRestaurantCart';
import { ShiftPanel } from './ShiftPanel';
import { TablePicker } from './TablePicker';
import { ProductGrid } from './ProductGrid';
import { OrderCart } from './OrderCart';
import { OrderSummary } from './OrderSummary';
import { ActionButtons } from './ActionButtons';
import { AdminPinModal } from './AdminPinModal';
import { WaiterAuthModal } from './WaiterAuthModal';
import { PinOnlyAuthModal } from './PinOnlyAuthModal';
import { ShiftOpenModal } from './modals/ShiftOpenModal';
import { NewOrderModal } from './modals/NewOrderModal';
import { CashierActionPinModal } from './modals/CashierActionPinModal';
import { CloseShiftModal } from './modals/CloseShiftModal';
import { AddTableModal } from './AddTableModal';
import { jwtDecode } from 'jwt-decode';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import useRestaurantPOSStore from '../../store/restaurantPOS';

const RestaurantPOS = () => {
  // ─── HOOKS (must all be here, no early returns before this section ends) ───

  const navigate = useNavigate();
  const { user, isAuthenticated, loading, getAccessToken } = useAuth();

  const [checkingAuth, setCheckingAuth] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [products, setProducts] = useState([]);
  const [tables, setTables] = useState([]);
  const [waiters, setWaiters] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [tableNumber, setTableNumber] = useState('1');
  const [transactionType, setTransactionType] = useState('dine-in');
  const [adminMode, setAdminMode] = useState(false);
  const [showAdminPin, setShowAdminPin] = useState(false);
  const [pendingAdminAction, setPendingAdminAction] = useState(null);
  const [showWaiterAuth, setShowWaiterAuth] = useState(false);
  const [waiterError, setWaiterError] = useState(null);
  const [showSessionTimeout, setShowSessionTimeout] = useState(false);
  const [sessionTimeoutError, setSessionTimeoutError] = useState(null);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [showShiftOpen, setShowShiftOpen] = useState(false);
  const [showCashierPin, setShowCashierPin] = useState(false);
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [showAddTable, setShowAddTable] = useState(false);
  const [showCloseShift, setShowCloseShift] = useState(false);
  const [showCashierAction, setShowCashierAction] = useState(false);
  const [cashierAction, setCashierAction] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [openPaymentModalToken, setOpenPaymentModalToken] = useState(0);
  const [billOutTotalDue, setBillOutTotalDue] = useState(0);
  const [billOutMode, setBillOutMode] = useState(false);
  // 'tables' = show TablePicker; 'products' = show ProductGrid (after a table is chosen)
  const [leftPanelView, setLeftPanelView] = useState('tables');
  const cashierPinRef = useRef('');

  const shift = useShift();
  const cart = useRestaurantCart(tableNumber, transactionType);

  const existingSections = useMemo(() => {
    if (!Array.isArray(tables)) return [];
    return [...new Set(tables.map((t) => t.section).filter(Boolean))];
  }, [tables]);

  const normalizeToArray = useCallback((payload, nestedKeys = []) => {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== 'object') return [];
    for (const key of nestedKeys) {
      const value = payload?.[key];
      if (Array.isArray(value)) return value;
    }
    return [];
  }, []);

  const pickFirstArray = useCallback((candidates = []) => {
    for (const candidate of candidates) {
      if (Array.isArray(candidate) && candidate.length > 0) return candidate;
    }
    return [];
  }, []);

  const refreshTables = useCallback(async () => {
    try {
      const response = await restaurantAPI.tables.getAll();
      const tableList = pickFirstArray([
        normalizeToArray(response),
        normalizeToArray(response, ['data', 'tables']),
        normalizeToArray(response?.data),
        normalizeToArray(response?.data, ['tables', 'data']),
      ]);
      setTables(Array.isArray(tableList) ? tableList : []);
      return tableList;
    } catch (err) {
      setError('Failed to load tables');
      setTables([]);
      throw err;
    }
  }, [normalizeToArray, pickFirstArray]);

  const getCurrentUser = useCallback(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return null;
    try { return JSON.parse(storedUser); } catch { return null; }
  }, []);

  const token = isAuthenticated ? getAccessToken() : null;

  const getEmployeeId = useCallback(() => {
    const u = getCurrentUser();

    // Order creation endpoint expects waiter ObjectId (id/_id), not employeeId string.
    if (u?._id || u?.id || u?.userId) return u?._id || u?.id || u?.userId;
    if (u?.userData?._id || u?.userData?.id || u?.userData?.userId) {
      return u?.userData?._id || u?.userData?.id || u?.userData?.userId;
    }

    if (user?._id || user?.id || user?.userId) return user?._id || user?.id || user?.userId;
    if (user?.userData?._id || user?.userData?.id || user?.userData?.userId) {
      return user?.userData?._id || user?.userData?.id || user?.userData?.userId;
    }

    try {
      if (token) {
        const decoded = jwtDecode(token);
        if (decoded?._id || decoded?.id || decoded?.userId) {
          return decoded?._id || decoded?.id || decoded?.userId;
        }
      }
    } catch (decodeError) {
      console.error('Failed to decode token:', decodeError);
    }
    return null;
  }, [getCurrentUser, token, user]);

  const getCashierEmployeeId = useCallback(() => {
    const u = getCurrentUser();
    return (
      u?.employeeId ||
      u?.empId ||
      u?.employee_id ||
      u?.userData?.employeeId ||
      user?.employeeId ||
      user?.empId ||
      user?.employee_id ||
      user?.userData?.employeeId ||
      ''
    );
  }, [getCurrentUser, user]);

  const getTableIdByNumber = useCallback(() => {
    if (!Array.isArray(tables)) return null;
    const table = tables.find(
      (entry) => String(entry.number || entry.tableNumber) === String(tableNumber)
    );
    return table?._id || null;
  }, [tables, tableNumber]);

  // ─── ALL useEffect CALLS ───────────────────────────────────────────────────

  // 1. Context options — gate for checkingAuth
  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated && !isTerminalActivated()) return;
    getContextOptions()
      .then(() => setCheckingAuth(false))
      .catch((err) => {
        if (err?.response?.status === 401) {
          // Silent refresh will fire from the axios interceptor; if it fails
          // we stay on the POS via the terminal-activated flag.
          setCheckingAuth(false);
        } else {
          setAuthError('Failed to load context. Please refresh.');
          setCheckingAuth(false);
        }
      });
  }, [loading, isAuthenticated]);

  // 2. Load products and tables after auth confirmed
  useEffect(() => {
    if (checkingAuth) return;
    const loadInitialData = async () => {
      try {
        setPageLoading(true);
        const productsResponse = await productAPI.getAll();
        const productsData = pickFirstArray([
          normalizeToArray(productsResponse),
          normalizeToArray(productsResponse, ['data', 'products']),
          normalizeToArray(productsResponse?.data),
          normalizeToArray(productsResponse?.data, ['products', 'data']),
        ]);
        const tablesData = await refreshTables();
        console.log('📊 Loaded products:', productsData.length, 'tables:', Array.isArray(tablesData) ? tablesData.length : 0);
        setProducts(Array.isArray(productsData) ? productsData : []);
        setError(null);
      } catch (loadError) {
        if (loadError?.response?.status === 401) {
          setAuthError('Session expired. Please log in.');
        } else {
          console.error('Failed to load data:', loadError);
          setError('Failed to load products and tables');
        }
      } finally {
        setPageLoading(false);
      }
    };
    loadInitialData();
  }, [checkingAuth, pickFirstArray, normalizeToArray, refreshTables]);

  // 3. Refresh tables every 15s
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      refreshTables().catch((err) => console.warn('Failed to refresh tables:', err));
    }, 15000);
    return () => window.clearInterval(intervalId);
  }, [refreshTables]);

  // 4. Load waiters
  useEffect(() => {
    const loadWaiters = async () => {
      try {
        const response = await restaurantAPI.employees.getWaiters();
        const waiterList = pickFirstArray([
          normalizeToArray(response),
          normalizeToArray(response, ['data', 'employees']),
          normalizeToArray(response?.data),
          normalizeToArray(response?.data, ['employees', 'data']),
        ]);
        setWaiters(Array.isArray(waiterList) ? waiterList : []);
      } catch (err) {
        console.error('❌ Failed to load waiters:', err);
        setWaiters([]);
      }
    };
    loadWaiters();
  }, [normalizeToArray, pickFirstArray]);

  // 5. Clear cart on table change
  useEffect(() => {
    setCurrentOrderId(null);
    cart.clearCart();
  }, [tableNumber]);

  // 5a. Return to table picker when the active table is deselected
  useEffect(() => {
    if (!selectedTable) setLeftPanelView('tables');
  }, [selectedTable]);

  // 5b. Skip the table picker for takeout / delivery
  useEffect(() => {
    if (transactionType === 'takeout' || transactionType === 'delivery') {
      setLeftPanelView('products');
    } else if (!selectedTable) {
      setLeftPanelView('tables');
    }
  }, [transactionType]);

  // 6. Shift modal logic
  useEffect(() => {
    if (shift.loading) return;
    if (showSessionTimeout) {
      setShowCashierPin(false);
      setShowShiftOpen(false);
      return;
    }
    if (!shift.isShiftActive) {
      if (!shift.shift) {
        setShowShiftOpen(true);
        setShowCashierPin(false);
      } else {
        setShowCashierPin(true);
        setShowShiftOpen(false);
      }
    } else {
      setShowShiftOpen(false);
      setShowCashierPin(false);
    }
  }, [shift.isShiftActive, shift.shift, showSessionTimeout, shift.loading]);

  // 7. Session timeout event listener
  useEffect(() => {
    const handleSessionTimeoutEvent = async (event) => {
      let resolvedShift = shift.shift;
      if (!resolvedShift) resolvedShift = await shift.checkShiftStatus();
      if (!resolvedShift) {
        setShowSessionTimeout(false);
        setShowShiftOpen(true);
        setSessionTimeoutError('No active shift found. Please start a new shift.');
        return;
      }
      setShowSessionTimeout(true);
      setSessionTimeoutError(
        event.detail?.error || 'Session expired. Please verify your PIN to continue.'
      );
    };
    window.addEventListener('session-timeout', handleSessionTimeoutEvent);
    return () => window.removeEventListener('session-timeout', handleSessionTimeoutEvent);
  }, [shift]);

  // ─── EARLY RETURNS (after every single hook and useEffect) ────────────────

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        Loading terminal...
      </div>
    );
  }

  // Terminal-mode guard: a refreshed page may briefly have no in-memory user
  // session, but if the terminal was previously activated by a manager we keep
  // the POS usable. Only show the setup screen when the terminal has NEVER
  // been activated on this device.
  if (!isAuthenticated && !isTerminalActivated()) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <h2>Terminal not set up</h2>
        <p>A manager must log in once to activate this terminal.</p>
        <button onClick={() => navigate('/login')}>Manager login</button>
      </div>
    );
  }

  if (checkingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-100 z-50">
        <div className="bg-white p-8 rounded shadow text-center">
          <h2 className="text-xl font-bold mb-2">Checking authentication…</h2>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-100 z-50">
        <div className="bg-white p-8 rounded shadow text-center">
          <h2 className="text-xl font-bold mb-2">{authError}</h2>
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ─── HANDLERS ─────────────────────────────────────────────────────────────

  const handleSelectProduct = (product) => {
    cart.addItem(product, 1);
    setSuccessMessage(`${product.name} added to cart`);
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  const handleShiftOpen = async ({ employeeId, pin, startingDenominations }) => {
    try {
      await shift.startShift(employeeId, pin, startingDenominations);
      await shift.checkShiftStatus();
      setShowShiftOpen(false);
      setSuccessMessage('Shift started successfully');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (openError) {
      const message = openError?.response?.data?.message || openError?.message || '';
      if (message.toLowerCase().includes('already has an open shift')) {
        try {
          const existingResponse = await restaurantAPI.shifts.getActiveByCashier(employeeId);
          const payload =
            existingResponse?.data && typeof existingResponse.data === 'object'
              ? existingResponse.data
              : existingResponse;
          if (payload?.shift) {
            shift.setShiftData?.(payload.shift);
            setShowShiftOpen(false);
            setShowCashierPin(true);
            setSuccessMessage('Shift found. Please enter your PIN to continue.');
            setTimeout(() => setSuccessMessage(null), 3000);
            return;
          }
        } catch (resolveError) {
          console.warn('Failed to resolve existing open shift by cashier:', resolveError);
        }
      }
      throw openError;
    }
  };

  const handleSessionTimeoutAuth = async (pin) => {
    try {
      setSessionTimeoutError(null);
      setPageLoading(true);
      let currentShift = shift.shift;
      if (!currentShift || !currentShift._id) {
        currentShift = await shift.checkShiftStatus();
        if (!currentShift || !currentShift._id) {
          setShowSessionTimeout(false);
          setShowShiftOpen(true);
          setSessionTimeoutError('No active shift found. Please start a new shift.');
          return;
        }
      }
      const response = await restaurantAPI.shifts.verifyPin(currentShift._id, pin);
      if (!response.data.success) throw new Error('Invalid PIN');
      setShowSessionTimeout(false);
      setShowCashierPin(false);
      setSessionTimeoutError(null);
      setSuccessMessage('Welcome back! Your shift is active.');
      setTimeout(() => setSuccessMessage(null), 2000);
      if (shift.checkShiftStatus) await shift.checkShiftStatus();
    } catch (authError) {
      setSessionTimeoutError(
        authError.response?.data?.message || authError.message || 'PIN verification failed'
      );
      throw authError;
    } finally {
      setPageLoading(false);
    }
  };

  const handleSendOrder = async ({ verifiedWaiterId } = {}) => {
    try {
      setPageLoading(true);
      setError(null);
      if (cart.items.length === 0) { setError('Cart is empty'); return; }
      const tableId = getTableIdByNumber();
      if (!tableId) { setError(`Table ${tableNumber} not found`); return; }
      const employeeId = verifiedWaiterId || getEmployeeId();
      if (!employeeId) { setError('Waiter identity missing. Please re-authenticate.'); return; }
      // createOrder is called without a PIN — waiter was already verified upstream
      const orderResponse = await cart.createOrder(tableId, employeeId);
      const orderId =
        orderResponse?.data?.order?._id ||
        orderResponse?.data?.order?.id ||
        orderResponse?.data?._id ||
        orderResponse?.data?.id ||
        orderResponse?.order?._id ||
        orderResponse?.order?.id ||
        orderResponse?._id ||
        orderResponse?.id ||
        null;
      if (!orderId) throw new Error('Order created but ID not found in response');
      setCurrentOrderId(orderId);
      try {
        await restaurantAPI.orders.send(orderId);
        setSuccessMessage(`Order #${String(orderId).slice(-6)} sent to kitchen!`);
      } catch (sendError) {
        console.warn('Send order failed (non-blocking):', sendError);
        setSuccessMessage(`Order #${String(orderId).slice(-6)} created (print may not be available)`);
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (sendOrderError) {
      console.error('Send order error:', sendOrderError);
      setError(sendOrderError.message || 'Failed to send order');
    } finally {
      setPageLoading(false);
    }
  };

  const handleSubmitOrder = async (paymentMethod, tendered) => {
    try {
      setPageLoading(true);
      setError(null);
      if (!selectedTable) throw new Error('Select a table before processing payment');
      const selectedTableData = Array.isArray(tables)
        ? tables.find((entry) => String(entry?._id) === String(selectedTable))
        : null;
      const orderRef = selectedTableData?.currentOrder;
      const orderId = typeof orderRef === 'string' ? orderRef : orderRef?._id || orderRef?.id;
      if (!orderId) throw new Error('No active order found for the selected table');
      const billingResponse = await restaurantAPI.billing.generate(orderId);
      const billingPayload = billingResponse?.data || billingResponse;
      const billingId = billingPayload?.billing?._id || billingPayload?.billing?.id;
      const totalDue = Number(billingPayload?.totalDue || billingPayload?.billing?.totalAmount || 0);
      if (!billingId) throw new Error('Billing generation failed');
      const normalizedMethod = ['gcash', 'maya'].includes(paymentMethod) ? 'charge' : paymentMethod;
      const paidAmount = Number(tendered || 0);
      if (normalizedMethod === 'cash' && paidAmount < totalDue) throw new Error('Insufficient amount');
      if (!cashierPinRef.current) {
        // Re-open the cashier PIN modal exactly once so the user can re-verify.
        setCashierAction('bill_out');
        setShowCashierAction(true);
        throw new Error('Cashier verification required. Please enter PIN.');
      }
      try {
        await restaurantAPI.billing.processPay(billingId, cashierPinRef.current, [{
          paymentType: normalizedMethod,
          amount: totalDue,
          tendered: normalizedMethod === 'cash' ? paidAmount : totalDue,
        }]);
      } catch (payError) {
        // If the backend rejected the cached PIN, clear it and surface an
        // inline error rather than silently asking again.
        if (payError?.response?.status === 401 || payError?.response?.status === 403) {
          cashierPinRef.current = '';
          throw new Error('Cashier PIN no longer valid. Please verify PIN again.');
        }
        throw payError;
      }
      try { await restaurantAPI.printing.printReceipt(orderId); }
      catch (printError) { console.warn('Print receipt failed (non-blocking):', printError); }
      cashierPinRef.current = '';
      await refreshTables();
      setSelectedTable(null);
      setCashierAction(null);
      setBillOutMode(false);
      setBillOutTotalDue(0);
      setOpenPaymentModalToken(0);
      setSuccessMessage('Payment processed and receipt printed');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (submitError) {
      console.error('Submit order error:', submitError);
      setError(submitError.message || 'Failed to submit order');
    } finally {
      setPageLoading(false);
    }
  };

  const handlePrintKitchen = async (orderId) => {
    const actualOrderId = typeof orderId === 'object' ? orderId?._id || orderId?.id : orderId;
    if (!actualOrderId) { setError('No order ID provided for printing'); return; }
    try {
      setPageLoading(true); setError(null);
      await restaurantAPI.orders.send(actualOrderId);
      setSuccessMessage('Kitchen prints sent!');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (printError) {
      console.warn('Print kitchen not available:', printError);
      setSuccessMessage('Order received (printer may not be available in dev)');
      setTimeout(() => setSuccessMessage(null), 2000);
    } finally { setPageLoading(false); }
  };

  const handlePrintBar = async (orderId) => {
    const actualOrderId = typeof orderId === 'object' ? orderId?._id || orderId?.id : orderId;
    if (!actualOrderId) { setError('No order ID provided for printing'); return; }
    try {
      setPageLoading(true); setError(null);
      await restaurantAPI.orders.send(actualOrderId);
      setSuccessMessage('Bar prints sent!');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (printError) {
      console.warn('Print bar not available:', printError);
      setSuccessMessage('Order received (printer may not be available in dev)');
      setTimeout(() => setSuccessMessage(null), 2000);
    } finally { setPageLoading(false); }
  };

  const handleParkOrder = async () => {
    try {
      setPageLoading(true); setError(null);
      throw new Error('Park order functionality requires the order transfer feature. Create the order normally and transfer it to another table if needed.');
    } catch (parkError) {
      setError(parkError.message || 'Park order is not available in the new system');
    } finally { setPageLoading(false); }
  };

  const handleAdminVerify = async (pin) => {
    if (pin !== '1234') throw new Error('Invalid PIN');
    setAdminMode(true);
    setShowAdminPin(false);
    if (pendingAdminAction) setPendingAdminAction(null);
    setSuccessMessage('Admin mode enabled');
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  const handleRemoveItemWithPin = (productId) => {
    if (adminMode) {
      cart.removeItem(productId);
      setSuccessMessage('Item removed');
      setTimeout(() => setSuccessMessage(null), 1500);
    } else {
      setPendingAdminAction(() => () => cart.removeItem(productId));
      setShowAdminPin(true);
    }
  };

  const handlePrintBill = (tableId) => {
    if (!tableId) { setError('Select a valid table first'); return; }
    setSelectedTable(tableId);
    setCashierAction('print_bill');
    setShowCashierAction(true);
  };

  const handleBillOut = (tableId) => {
    if (!tableId) { setError('Select a valid table first'); return; }
    setSelectedTable(tableId);
    setCashierAction('bill_out');
    setShowCashierAction(true);
  };

  const handleCashierActionVerify = async (pin) => {
    if (!shift.shift?._id) throw new Error('No active shift found');
    const response = await restaurantAPI.shifts.verifyPin(shift.shift._id, pin);
    if (!response.data.success) throw new Error('Invalid PIN');
    setShowCashierAction(false);
    cashierPinRef.current = pin;
    if (cashierAction === 'print_bill') {
      await restaurantAPI.printing.printBill(selectedTable);
      setSuccessMessage('Billing statement printed');
      setTimeout(() => setSuccessMessage(null), 2000);
    }
    if (cashierAction === 'bill_out') {
      const selectedTableData = Array.isArray(tables)
        ? tables.find((entry) => String(entry?._id) === String(selectedTable))
        : null;
      const currentOrder = selectedTableData?.currentOrder;
      const totalDue = Number((typeof currentOrder === 'object' && currentOrder?.totalAmount) || 0);
      setBillOutTotalDue(totalDue);
      setBillOutMode(true);
      setOpenPaymentModalToken((current) => current + 1);
    }
  };

  const handleTableCreated = useCallback(async (newTable) => {
    setShowAddTable(false);
    await refreshTables();
    const id = newTable?._id || newTable?.id;
    const tableNum = String(newTable?.tableNumber || newTable?.number || newTable?.name || '');
    setSelectedTable(id);
    setTableNumber(tableNum);
    setLeftPanelView('products');
    setSuccessMessage(`Table "${newTable?.tableNumber || tableNum}" created and selected`);
    setTimeout(() => setSuccessMessage(null), 2500);
  }, [refreshTables]);

  const handleOrderSent = (orderId, tableId) => {
    setCurrentOrderId(orderId);
    setSelectedTable(tableId);
    const safeTables = Array.isArray(tables) ? tables : [];
    const selected = safeTables.find((entry) => entry._id === tableId);
    if (selected) {
      setTableNumber(String(selected.number || selected.tableNumber || tableNumber));
    }
    setTables((previous) => {
      const safePrevious = Array.isArray(previous) ? previous : [];
      return safePrevious.map((entry) =>
        entry._id === tableId ? { ...entry, status: 'occupied' } : entry
      );
    });
    setSuccessMessage('Order sent to kitchen/bar');
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────

  return (
    <div className="h-screen bg-gray-100 flex flex-col relative">
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Restaurant POS</h1>
            <p className="text-sm text-gray-600 mt-1">
              Table: <span className="font-semibold">{tableNumber}</span> • Type:{' '}
              <span className="font-semibold">
                {transactionType.charAt(0).toUpperCase() + transactionType.slice(1)}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowNewOrder(true)}
              disabled={!shift.isShiftActive}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2 shadow-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlusIcon className="h-5 w-5" />
              New Order
            </button>
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">
                Cashier: {user?.name || user?.username || user?.email || 'User'}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {shift.shift && `Shift Started: ${new Date(shift.shift.startTime).toLocaleTimeString()}`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-medium text-red-800">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mx-4 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-3">
          <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-medium text-green-800">{successMessage}</p>
        </div>
      )}

      <div className={`flex-1 overflow-hidden p-4 ${!shift.isShiftActive ? 'pointer-events-none select-none' : ''}`}>
        <div className="h-full flex gap-4">
          <div className="flex-1 bg-white rounded-lg shadow-sm overflow-hidden flex flex-col">
            {leftPanelView === 'tables' ? (
              <TablePicker
                tables={tables}
                selectedTableId={selectedTable}
                loading={pageLoading}
                onSelect={(table) => {
                  const id = table._id || table.id;
                  setSelectedTable(id);
                  setTableNumber(String(table.number || table.tableNumber || ''));
                  setLeftPanelView('products');
                }}
                onAddTable={() => setShowAddTable(true)}
              canAddTable={['cashier', 'manager', 'admin'].includes(user?.role || user?.userData?.role || '')}
              />
            ) : (
              <>
                <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                  <div className="text-sm">
                    <span className="text-gray-600">Table:</span>{' '}
                    <span className="font-bold text-gray-900">T{tableNumber}</span>
                    <span className="ml-3 text-gray-600">Type:</span>{' '}
                    <span className="font-semibold text-gray-900 capitalize">{transactionType}</span>
                  </div>
                  {transactionType === 'dine-in' && (
                    <button
                      type="button"
                      onClick={() => setLeftPanelView('tables')}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      ← Change table
                    </button>
                  )}
                </div>
                <div className="flex-1 overflow-hidden">
                  <ProductGrid
                    products={products}
                    onSelectProduct={handleSelectProduct}
                    loading={pageLoading}
                    cartItems={cart.items}
                  />
                </div>
              </>
            )}
          </div>
          <div className="w-96 flex flex-col bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <ShiftPanel
                isShiftActive={shift.isShiftActive}
                shift={shift.shift}
                onStartShift={shift.startShift}
                onEndShiftClick={() => setShowCloseShift(true)}
                loading={shift.loading}
                error={shift.error}
                userName={user?.name || user?.email}
              />
            </div>
            <div className="p-4 border-b border-gray-200">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Order Type</label>
              <select
                value={transactionType}
                onChange={(e) => { setTransactionType(e.target.value); cart.clearCart(); }}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              >
                <option value="dine-in">Dine-In</option>
                <option value="takeout">Takeout</option>
                <option value="delivery">Delivery</option>
              </select>
            </div>
            <div className="flex-1 overflow-hidden">
              <OrderCart
                items={cart.items}
                onUpdateQuantity={cart.updateQuantity}
                onRemoveItem={handleRemoveItemWithPin}
                onUpdateNotes={cart.updateItemNotes}
                onClearCart={cart.clearCart}
                loading={pageLoading}
                adminMode={adminMode}
              />
            </div>
            {!billOutMode && cart.items.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">{cart.itemCount} item{cart.itemCount !== 1 ? 's' : ''}</span>
                  <span className="font-semibold text-gray-900">
                    ₱{Number(cart.totals.subtotal || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
            {billOutMode && cart.items.length > 0 && (
              <OrderSummary
                itemCount={cart.itemCount}
                subtotal={cart.totals.subtotal}
                discount={cart.totals.discount}
                tax={cart.totals.tax}
                total={cart.totals.total}
                discountPercent={cart.discountPercent}
                onDiscountChange={cart.setDiscountPercent}
                taxPercent={cart.taxPercent}
                onTaxChange={cart.setTaxPercent}
                transactionType={transactionType}
              />
            )}
            <ActionButtons
              itemCount={cart.itemCount}
              total={cart.totals.total}
              paymentTotalOverride={billOutTotalDue}
              allowEmptyCartPayment={billOutMode}
              selectedTableId={getTableIdByNumber()}
              currentOrderId={currentOrderId}
              onSendOrder={() => {
                setWaiterError(null);
                setShowWaiterAuth(true);
              }}
              onSubmitOrder={handleSubmitOrder}
              onPrintBill={handlePrintBill}
              onBillOut={handleBillOut}
              onPrintKitchen={handlePrintKitchen}
              onPrintBar={handlePrintBar}
              onParkOrder={handleParkOrder}
              loading={pageLoading}
              error={cart.error}
              transactionType={transactionType}
              openPaymentModalToken={openPaymentModalToken}
            />
          </div>
        </div>
      </div>

      <CloseShiftModal
        isOpen={showCloseShift}
        shift={shift.shift}
        onClose={() => setShowCloseShift(false)}
        onShiftClosed={() => {
          setShowCloseShift(false);
          shift.clearShift?.();
          navigate('/pos/shift-start');
        }}
      />
      <ShiftOpenModal
        isOpen={showShiftOpen}
        cashierName={user?.name || user?.email}
        defaultEmployeeId={getCashierEmployeeId()}
        onSubmit={handleShiftOpen}
        loading={shift.loading}
        error={shift.error}
      />
      <PinOnlyAuthModal
        isOpen={showCashierPin}
        onClose={() => { setShowCashierPin(false); setSessionTimeoutError(null); }}
        onVerify={handleSessionTimeoutAuth}
        loading={pageLoading}
        error={sessionTimeoutError}
        title="Continue Your Shift"
        description="Enter your PIN to continue working on your shift."
        cashierName={user?.name || user?.email || 'Cashier'}
      />
      <AddTableModal
        isOpen={showAddTable}
        onClose={() => setShowAddTable(false)}
        onTableCreated={handleTableCreated}
        loading={pageLoading}
        existingSections={existingSections}
      />
      <NewOrderModal
        isOpen={showNewOrder}
        onClose={() => setShowNewOrder(false)}
        tables={tables}
        waiters={waiters}
        products={products}
        onOrderSent={handleOrderSent}
        loading={pageLoading}
      />
      <CashierActionPinModal
        isOpen={showCashierAction}
        action={cashierAction}
        cashierName={user?.name || user?.email}
        onVerify={handleCashierActionVerify}
        onClose={() => setShowCashierAction(false)}
        loading={pageLoading}
        error={error}
      />
      <AdminPinModal
        isOpen={showAdminPin}
        onClose={() => { setShowAdminPin(false); setPendingAdminAction(null); }}
        onVerify={handleAdminVerify}
        loading={pageLoading}
        error={shift.error}
        title="Admin Authentication"
        description="Enter admin PIN to remove items"
      />
      <WaiterAuthModal
        isOpen={showWaiterAuth}
        waiters={waiters}
        title="Waiter Authentication"
        description="Select your name and PIN to send this order"
        loading={pageLoading}
        error={waiterError}
        onClose={() => { setShowWaiterAuth(false); setWaiterError(null); }}
        onVerify={async (waiterId, pin) => {
          // Single PIN verification. On success the modal closes immediately
          // and the order is sent using the already-verified waiter identity.
          // The PIN is NOT re-sent to createOrder.
          setWaiterError(null);
          setPageLoading(true);
          try {
            const response = await restaurantAPI.shifts.verifyWaiter(waiterId, pin);
            if (!response?.data?.success) {
              // Show invalid PIN inline; keep modal open; do NOT throw.
              setWaiterError(response?.data?.message || 'Invalid PIN');
              setPageLoading(false);
              return;
            }
            const verifiedWaiterId = response.data?.user?._id || waiterId;
            // Close the modal immediately so the user never sees a second prompt.
            setShowWaiterAuth(false);
            setWaiterError(null);
            await handleSendOrder({ verifiedWaiterId });
          } catch (verifyError) {
            const status = verifyError?.response?.status;
            const message =
              verifyError?.response?.data?.message ||
              (status === 401 ? 'Invalid PIN' : verifyError.message) ||
              'Authentication failed';
            setWaiterError(message);
            // Do NOT re-throw — re-throwing causes the modal to flash and looks
            // like a second prompt.
          } finally {
            setPageLoading(false);
          }
        }}
      />
      <PinOnlyAuthModal
        isOpen={showSessionTimeout}
        onClose={() => { setShowSessionTimeout(false); setSessionTimeoutError(null); }}
        onVerify={handleSessionTimeoutAuth}
        loading={pageLoading}
        error={sessionTimeoutError}
        title="Session Expired"
        description="Your session has expired. Enter your PIN to continue working on your shift."
        cashierName={user?.name || user?.email || 'Cashier'}
      />
    </div>
  );
};

export default RestaurantPOS;